import React, { useEffect, useState } from 'react';

import classNames from 'classnames';
import moment from 'moment';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import { useStepper } from 'components/hooks/useStepper';

import ModalBox from 'components/application/ModalBox';
import Stepper from 'components/application/Stepper';

import Download from 'components/attachments/components/Download';
import ProcessingNotice from 'components/attachments/components/ProcessingNotice';
import Display from 'components/training/attachments/components/Display';

import { resourceShape } from 'components/helpers/serialisableResources';

export default function TrainingModalBox({ attachments = [], data }) {
  const [isOpen, setIsOpen] = useState(false);
  const [timestamp, setTimestamp] = useState(null);

  const [step, stepDown, stepUp, resetStep] = useStepper(
    data.attachments.length,
  );

  // data.attachments is snapshotted when the modal is opened, so the live collection is
  // read through for attributes that change while it is open, such as processing state
  const attachmentsIndexedById = attachments.reduce((acc, attachment) => {
    acc[attachment.id] = attachment;
    return acc;
  }, {});

  const modalAttachments = data.attachments.map(
    (attachment) => attachmentsIndexedById[attachment.id] || attachment,
  );

  const { t: i18nT } = useTranslation('components', {
    keyPrefix: 'training.TrainingModalBox',
  });

  const openModalBox = () => {
    setIsOpen(true);
  };

  const closeModalBox = () => {
    setIsOpen(false);
  };

  useEffect(() => {
    if (data.loaded) {
      openModalBox();
    }
  }, [data]);

  useEffect(() => {
    setTimestamp(moment().unix());
    resetStep();
  }, [JSON.stringify(data.attachments)]);

  const selectedAttachment = modalAttachments[step - 1];
  const attachmentType = selectedAttachment?.attributes.recordType;
  const isSelectedAttachmentProcessing =
    !!selectedAttachment &&
    selectedAttachment.attributes.attachedProcessed === false;

  const evidenceAttachment = () => {
    if (modalAttachments.length === 0) {
      return false;
    }
    if (selectedAttachment) {
      return (
        <>
          {isSelectedAttachmentProcessing && <ProcessingNotice />}
          <Display
            attachedUrl={selectedAttachment.attributes.attachedUrl}
            recordType={selectedAttachment.attributes.recordType}
            timestamp={timestamp}
          />
        </>
      );
    }
  };

  const isNotesContext = data.context === 'notes';

  const courseName = data.course?.attributes.name;

  return (
    <ModalBox
      isOpen={isOpen}
      onClose={closeModalBox}
      secondaryFooter={
        data.context === 'attachments' && (
          <Stepper
            currentStep={step}
            onStepDown={stepDown}
            onStepUp={stepUp}
            totalSteps={data.attachments.length}
          />
        )
      }
    >
      <>
        <div className='modalbox-header tw-border-grey-100'>
          <div className='tw-flex tw-items-center tw-justify-between'>
            <div className='tw-min-w-0 tw-flex-1'>
              <h2 className='truncated-text-container tw-m-0 tw-p-0 tw-text-l tw-font-semibold tw-text-grey-900'>
                {isNotesContext ?
                  i18nT('title', {
                    courseName,
                    interpolation: { escapeValue: false },
                  })
                : courseName}
              </h2>
            </div>
            {data.context === 'attachments' && selectedAttachment && (
              <Download
                buttonText={i18nT('download.buttonText')}
                disabled={!selectedAttachment.attributes.attachedUrl}
                filename={selectedAttachment.attributes.filename}
                hashid={selectedAttachment.attributes.hashid}
              />
            )}
          </div>
        </div>
        <div
          className={classNames(
            'modalbox-body',
            attachmentType === 'pdf' &&
              data.context === 'attachments' &&
              !isSelectedAttachmentProcessing &&
              'tw-p-0',
          )}
        >
          {isNotesContext ?
            data.training?.attributes.notes
          : evidenceAttachment()}
        </div>
      </>
    </ModalBox>
  );
}

TrainingModalBox.propTypes = {
  attachments: PropTypes.arrayOf(resourceShape('attachment')),
  data: PropTypes.object.isRequired,
};
